"use client";

import { useState, useTransition, useRef, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { PostcodeInput } from "@/app/components/PostcodeInput";
import { isValidPostcodeFormat } from "@/lib/postcode";
import { getTradeName } from "@/lib/trades";
import { createClient } from "@/lib/supabase";
import {
  analyseSnapPhoto,
  type SnapResult,
} from "@/app/actions/snap";
import {
  searchNearbyTradespeople,
  type NearbyTradesperson,
} from "@/app/actions/search";
import { ShareButton } from "@/app/components/ShareButton";
import { prepareSnapUpload } from "@/lib/snap-upload";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_SIZE = 5 * 1024 * 1024;

export function SnapClient() {
  const searchParams = useSearchParams();
  const postcodeParam = searchParams.get("postcode");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [postcode, setPostcode] = useState(postcodeParam ?? "");
  const [previousPostcodeParam, setPreviousPostcodeParam] = useState(postcodeParam);
  if (previousPostcodeParam !== postcodeParam) {
    setPreviousPostcodeParam(postcodeParam);
    if (postcodeParam) setPostcode(postcodeParam);
  }
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SnapResult | null>(null);
  const [tradespeople, setTradespeople] = useState<NearbyTradesperson[]>([]);
  const [snapCount, setSnapCount] = useState<number | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isPending, startTransition] = useTransition();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  // Monotonic token so a superseded analysis (user reset or started again)
  // never applies its late async results over a newer one.
  const runIdRef = useRef(0);

  useEffect(() => {
    createClient()
      .auth.getUser()
      .then(({ data }) => {
        if (data.user) setIsLoggedIn(true);
      });

  }, []);

  // Revoke the object URL when the preview changes or the component unmounts,
  // so repeated uploads in one session don't leak memory.
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  function handleFile(f: File) {
    runIdRef.current++;
    setFile(null);
    setPreview(null);
    setError(null);
    setResult(null);
    setTradespeople([]);
    setSnapCount(null);

    if (!ALLOWED_TYPES.includes(f.type)) {
      setError("Please upload a JPEG, PNG, or WebP image.");
      return;
    }
    if (f.size > MAX_SIZE) {
      setError("Photo must be under 5 MB.");
      return;
    }

    setFile(f);
    const url = URL.createObjectURL(f);
    setPreview(url);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  }

  function handleAnalyse() {
    if (!file || !postcode.trim() || !isValidPostcodeFormat(postcode.trim()))
      return;

    setError(null);
    const myRun = ++runIdRef.current;

    startTransition(async () => {
      let response: Awaited<ReturnType<typeof analyseSnapPhoto>>;
      try {
        const upload = await prepareSnapUpload(file);
        if (runIdRef.current !== myRun) return;
        const formData = new FormData();
        formData.append("file", upload);
        formData.append("postcode", postcode.trim());
        response = await analyseSnapPhoto(formData);
      } catch {
        if (runIdRef.current === myRun) {
          setError("We couldn't upload or analyse that photo. Check your connection and try again, or choose a smaller photo.");
        }
        return;
      }
      const { result: snapResult, error: snapError } = response;

      // Bail if this run has been superseded (reset or a newer analysis).
      if (runIdRef.current !== myRun) return;

      if (snapError || !snapResult) {
        setError(
          snapError ??
            "Something went wrong. Please try again."
        );
        return;
      }

      setResult(snapResult);

      setTimeout(() => {
        resultsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);

      if (snapResult.nearbySnapCount > 0) {
        setSnapCount(snapResult.nearbySnapCount);
      }

      // Only look for tradespeople when we actually identified a home issue.
      if (snapResult.diagnosis.identified) {
        const { results } = await searchNearbyTradespeople(
          postcode.trim(),
          snapResult.diagnosis.trade_category_slug
        );
        if (runIdRef.current !== myRun) return;
        setTradespeople(results.slice(0, 5));
      }
    });
  }

  function resetForm() {
    runIdRef.current++;
    setFile(null);
    setPreview(null);
    setResult(null);
    setTradespeople([]);
    setSnapCount(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  const tradeName = result
    ? getTradeName(result.diagnosis.trade_category_slug)
    : "";
  const identified = result?.diagnosis.identified ?? false;
  const jobTypeName = result?.diagnosis.job_type_name ?? "";

  const postcodeValid =
    postcode.trim().length > 0 && isValidPostcodeFormat(postcode.trim());

  return (
    <div className="min-h-screen bg-warm-bg">
      {/* Hero */}
      <section className="bg-forest text-white py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
            Take a photo of your problem
          </h1>
          <p className="text-sage-light text-base sm:text-lg max-w-xl mx-auto">
            Snap a photo, enter your postcode, and get an instant AI diagnosis
            with a cost estimate and matched local tradespeople. Completely free.
          </p>
        </div>
      </section>

      <div className="max-w-2xl mx-auto px-4 sm:px-8 py-10 sm:py-14 space-y-8">
        {/* Upload area */}
        {!result && (
          <>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`relative border-2 border-dashed rounded-md p-8 sm:p-12 text-center cursor-pointer transition-all ${
                isDragging
                  ? "border-forest bg-forest/5"
                  : preview
                    ? "border-sage bg-white"
                    : "border-stone bg-white hover:border-sage-light"
              }`}
              role="button"
              tabIndex={0}
              aria-label="Upload a photo of your issue"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileInput}
                className="hidden"
                aria-hidden="true"
              />

              {preview ? (
                <div className="space-y-4">
                  <div className="relative w-full max-w-xs mx-auto aspect-[4/3] rounded-sm overflow-hidden">
                    <Image
                      src={preview}
                      alt="Your uploaded photo"
                      fill
                      className="object-cover"
                    />
                  </div>
                  <p className="text-sm text-text-muted">
                    {file?.name} &middot; Tap to change
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="w-16 h-16 mx-auto rounded-full bg-sage-pale flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-forest"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-semibold text-charcoal">
                    Take or upload a photo
                  </p>
                  <p className="text-xs text-text-muted">
                    JPEG, PNG, or WebP up to 5 MB
                  </p>
                </div>
              )}
            </div>

            {/* Postcode */}
            <div>
              <label className="text-sm font-semibold text-text-body mb-2 block">
                Your postcode
              </label>
              <PostcodeInput
                value={postcode}
                onChange={setPostcode}
                placeholder="e.g. SW1A 1AA"
                ariaLabel="Your postcode"
                required
              />
            </div>

            {/* Error */}
            {error && (
              <div className="p-4 bg-error/10 border border-error/20 rounded-sm">
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="button"
              onClick={handleAnalyse}
              disabled={!file || !postcodeValid || isPending}
              className="w-full py-4 bg-forest text-white font-semibold rounded-sm text-base disabled:opacity-50 disabled:cursor-not-allowed hover:bg-forest-light transition-colors"
            >
              {isPending ? (
                <span className="inline-flex items-center gap-3">
                  <LoadingSpinner />
                  Analysing your photo...
                </span>
              ) : (
                "Get instant diagnosis"
              )}
            </button>
          </>
        )}

        {/* Loading overlay (shown during transition) */}
        {isPending && !result && (
          <div className="text-center py-8">
            <div className="inline-flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-sage-pale flex items-center justify-center">
                <LoadingSpinner size="lg" />
              </div>
              <div>
                <p className="text-sm font-semibold text-charcoal">
                  Our AI is reviewing your photo
                </p>
                <p className="text-xs text-text-muted mt-1">
                  This usually takes a few seconds
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {result && (
          <div ref={resultsRef} className="space-y-8">
            {/* Safety warning (only when the AI flags a possible hazard) */}
            {result.diagnosis.safety_note && (
              <div
                role="alert"
                className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-md"
              >
                <svg
                  className="w-5 h-5 text-red-600 shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-bold text-red-800 mb-0.5">
                    Possible safety risk
                  </p>
                  <p className="text-sm text-red-700 leading-relaxed">
                    {result.diagnosis.safety_note}
                  </p>
                </div>
              </div>
            )}

            {/* Photo + diagnosis card */}
            <div className="bg-white rounded-md border border-stone/30 shadow-sm overflow-hidden">
              <div className="relative w-full aspect-[16/9]">
                <Image
                  src={result.photoUrl}
                  alt="Your uploaded photo"
                  fill
                  className="object-cover"
                />
              </div>
              <div className="p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center shrink-0 mt-0.5">
                    <svg
                      className="w-5 h-5 text-forest"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="font-serif text-lg font-bold text-charcoal mb-1">
                      AI Diagnosis
                    </h2>
                    <p className="text-sm text-text-body leading-relaxed">
                      {result.diagnosis.description}
                    </p>
                  </div>
                </div>

                {identified && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sage-pale rounded-full text-xs font-semibold text-forest">
                      {tradeName}
                    </span>
                    {jobTypeName && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cream rounded-full text-xs font-semibold text-charcoal">
                        {jobTypeName}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cream rounded-full text-xs font-semibold text-charcoal capitalize">
                      {result.diagnosis.scope} job
                    </span>
                  </div>
                )}

                {/* Price range */}
                {result.priceRange && (
                  <div className="bg-warm-bg rounded-md p-5 mt-4">
                    <p className="text-xs text-text-muted mb-2 uppercase tracking-widest font-semibold">
                      Estimated cost
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-forest tabular-nums">
                        &pound;{result.priceRange.low}
                      </span>
                      <span className="text-text-muted">&ndash;</span>
                      <span className="text-2xl font-bold text-forest tabular-nums">
                        &pound;{result.priceRange.high}
                      </span>
                    </div>
                    <p className="text-xs text-text-muted mt-2">
                      Based on typical{" "}
                      {jobTypeName
                        ? `${jobTypeName.toLowerCase()} work`
                        : `${tradeName.toLowerCase()} rates`}{" "}
                      in the {result.priceRange.regionName} area. Actual costs
                      depend on the specific work required.
                    </p>
                  </div>
                )}

                {/* AI disclaimer */}
                <p className="text-xs text-text-muted mt-4 leading-relaxed">
                  This is an AI-generated estimate based on a single photo, not a
                  professional inspection. Always confirm the problem and price
                  with a qualified tradesperson before any work begins.
                </p>

                {/* Share */}
                {result.id && identified && (
                  <div className="mt-4 pt-4 border-t border-stone/20">
                    <ShareButton
                      url={`/snap/${result.id}`}
                      title={`${tradeName} Diagnosis | Tradesfolk`}
                      text={`${result.diagnosis.description}${result.priceRange ? ` Estimated cost: £${result.priceRange.low} to £${result.priceRange.high}.` : ""} Get your own instant estimate at tradesfolk.co/snap`}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Nearby tradespeople */}
            {tradespeople.length > 0 && (
              <div>
                <h3 className="font-serif text-xl font-bold text-charcoal mb-4">
                  {tradeName}s near you
                </h3>
                <div className="space-y-3">
                  {tradespeople.map((person) => (
                    <Link
                      key={person.user_id}
                      href={`/tradesperson/${person.user_id}`}
                      className="flex items-center gap-4 bg-white rounded-md border border-stone/30 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all no-underline group"
                    >
                      {person.photo_url ? (
                        <Image
                          src={person.photo_url}
                          alt={`${person.first_name} ${person.last_name}`}
                          width={48}
                          height={48}
                          className="rounded-full object-cover shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-cream flex items-center justify-center shrink-0">
                          <span className="text-sm font-bold text-forest">
                            {person.first_name?.charAt(0)}
                            {person.last_name?.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-charcoal text-sm group-hover:text-forest transition-colors">
                          {person.first_name} {person.last_name}
                          {person.business_name && (
                            <span className="text-text-muted font-normal">
                              {" "}
                              &middot; {person.business_name}
                            </span>
                          )}
                        </p>
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                          {person.review_count > 0 && (
                            <span className="text-xs text-text-muted">
                              <span className="text-copper">&#9733;</span>{" "}
                              {person.avg_rating.toFixed(1)} (
                              {person.review_count} review
                              {person.review_count !== 1 ? "s" : ""})
                            </span>
                          )}
                          {person.distance_miles != null && (
                            <span className="text-xs text-text-muted">
                              {person.distance_miles.toFixed(1)} miles away
                            </span>
                          )}
                          {person.years_experience != null &&
                            person.years_experience > 0 && (
                              <span className="text-xs text-text-muted">
                                {person.years_experience} yr
                                {person.years_experience !== 1 ? "s" : ""} exp
                              </span>
                            )}
                        </div>
                      </div>
                      <svg
                        className="w-5 h-5 text-text-muted shrink-0 group-hover:text-forest transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  ))}
                </div>

                <div className="mt-6 text-center">
                  <Link
                    href={`/browse?trade=${result.diagnosis.trade_category_slug}&postcode=${encodeURIComponent(postcode)}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-forest hover:text-forest-light no-underline transition-colors"
                  >
                    View all {tradeName.toLowerCase()}s near {postcode}
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </div>
              </div>
            )}

            {/* CTA buttons */}
            <div className="space-y-3">
              {isLoggedIn ? (
                <Link
                  href={`/post-job?trade=${result.diagnosis.trade_category_slug}&postcode=${encodeURIComponent(postcode)}`}
                  className="block w-full py-4 bg-forest text-white font-semibold rounded-sm text-base text-center no-underline hover:bg-forest-light transition-colors"
                >
                  {identified
                    ? `Request quotes from ${tradeName.toLowerCase()}s`
                    : "Post a job and describe it"}
                </Link>
              ) : (
                <Link
                  href={`/signup/homeowner?redirect=${encodeURIComponent(`/post-job?trade=${result.diagnosis.trade_category_slug}&postcode=${encodeURIComponent(postcode)}`)}`}
                  className="block w-full py-4 bg-forest text-white font-semibold rounded-sm text-base text-center no-underline hover:bg-forest-light transition-colors"
                >
                  {identified
                    ? "Sign up free to request quotes"
                    : "Sign up free to post a job"}
                </Link>
              )}
              <button
                type="button"
                onClick={resetForm}
                className="w-full py-3 border border-forest text-forest font-semibold rounded-sm text-sm hover:bg-forest/5 transition-colors"
              >
                Analyse another photo
              </button>
            </div>

            {/* Related tools */}
            {identified && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link
                href={`/cost-estimator?trade=${result.diagnosis.trade_category_slug}&scope=${result.diagnosis.scope}&postcode=${encodeURIComponent(postcode)}`}
                className="flex items-center gap-3 p-4 bg-white rounded-md border border-stone/30 no-underline hover:border-sage-light transition-colors group"
              >
                <div className="w-9 h-9 rounded-full bg-sage-pale flex items-center justify-center shrink-0">
                  <svg className="w-4.5 h-4.5 text-forest" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-charcoal group-hover:text-forest transition-colors">Cost Estimator</p>
                  <p className="text-xs text-text-muted">Get a detailed cost breakdown</p>
                </div>
              </Link>
              <Link
                href={`/prices/${result.diagnosis.trade_category_slug}`}
                className="flex items-center gap-3 p-4 bg-white rounded-md border border-stone/30 no-underline hover:border-sage-light transition-colors group"
              >
                <div className="w-9 h-9 rounded-full bg-sage-pale flex items-center justify-center shrink-0">
                  <svg className="w-4.5 h-4.5 text-forest" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-charcoal group-hover:text-forest transition-colors">View {tradeName} Prices</p>
                  <p className="text-xs text-text-muted">See all job types and rates</p>
                </div>
              </Link>
            </div>
            )}

            {/* Ask the community CTA */}
            <Link
              href="/ask/new"
              className="flex items-center gap-3 p-4 bg-white rounded-md border border-stone/30 no-underline hover:border-sage-light transition-colors group"
            >
              <div className="w-9 h-9 rounded-full bg-sage-pale flex items-center justify-center shrink-0">
                <svg className="w-4.5 h-4.5 text-forest" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-charcoal group-hover:text-forest transition-colors">Want a second opinion?</p>
                <p className="text-xs text-text-muted">Ask the community for free advice</p>
              </div>
            </Link>

            {/* Social proof */}
            {snapCount !== null && snapCount > 0 && (
              <p className="text-center text-xs text-text-muted">
                {snapCount} homeowner{snapCount !== 1 ? "s" : ""} near you
                used Snap and Fix in the last 30 days
              </p>
            )}
          </div>
        )}

        {/* How it works (below form) */}
        {!result && (
          <div className="pt-4">
            <h2 className="font-serif text-xl font-bold text-charcoal mb-6 text-center">
              How it works
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                {
                  step: "1",
                  title: "Snap a photo",
                  desc: "Take or upload a photo of the issue in your home.",
                },
                {
                  step: "2",
                  title: "Get a diagnosis",
                  desc: "Our AI identifies the problem and estimates the cost.",
                },
                {
                  step: "3",
                  title: "Find your trade",
                  desc: "See matched local tradespeople and request quotes.",
                },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-forest text-white text-sm font-bold mb-3">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-charcoal text-sm mb-1">
                    {item.title}
                  </h3>
                  <p className="text-xs text-text-muted">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 p-5 bg-cream rounded-md text-center">
              <p className="text-sm text-text-body mb-2">
                No photo? Answer three quick questions instead.
              </p>
              <Link
                href="/diagnose"
                className="inline-flex items-center gap-2 text-sm font-semibold text-forest no-underline hover:underline"
              >
                Try the guided diagnosis tool →
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LoadingSpinner({ size = "sm" }: { size?: "sm" | "lg" }) {
  const dim = size === "lg" ? "w-8 h-8" : "w-5 h-5";
  return (
    <svg
      className={`${dim} animate-spin text-current`}
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}
